// Offscreen name and mass bitmaps (architecture §9.4, visuals §5): Ubuntu 400 (bundled, Ubuntu Font
// Licence; render/fonts.ts registers it), white fill with a black stroke of 0.1 × font drawn first
// with miter joins (colours from ARENA_COLORS[style][theme].nameFill / nameOutline). A bitmap is
// 1.4 × font tall and text width + 2 × 0.19 × font wide. Font sizes are
// quantised up to a ×1.25 ladder from 12 px (12, 15, 19, 24, 30 …; each step rounds the previous
// × 1.25) and drawn downscaled (never up: upscaled bitmaps blur). An LRU of 512
// entries keyed by (text, sizeStep, theme). Mass labels are keyed by the integer value; callers
// refresh a cell's mass label at most every 100 ms.
//
// Bitmaps are rendered at most at ladder step TEXT_CACHE_MAX_STEP (148 px); for bigger text the
// renderer draws directly (`drawTextDirect`), which is crisp and rare (huge cells seen up close).
//
// Implementer: client render. Consumer: render/renderer.ts.

import { ARENA_COLORS, RENDER_STYLES, type RenderStyle, type Rgb, rgbToHex } from '@ballio/shared';
import { type Canvas2D, type CanvasFactory, createCanvas, type Ctx2D, get2d } from './canvas.js';

/** A cached text bitmap. Draw it scaled by `fontPx / renderedPx`. */
export interface TextSprite {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  /** Bitmap size in px (text plus outline padding). */
  readonly width: number;
  readonly height: number;
  /** The ladder font size it was rendered at. */
  readonly renderedPx: number;
}

/** Cache key for colours: e.g. `classic-light`, `sketch-dark`. */
export type TextTheme = string;

export const TEXT_LADDER_BASE_PX = 12;
export const TEXT_LADDER_RATIO = 1.25;

/** The font stack: bundled Ubuntu (visuals §5), then common fallbacks. */
export const TEXT_FONT_FAMILY = 'Ubuntu, "Trebuchet MS", "Segoe UI", system-ui, sans-serif';
export const TEXT_FONT_WEIGHT = 400;

/** Bitmap height and side padding as multiples of the font size (visuals §5). */
export const TEXT_BITMAP_HEIGHT = 1.4;
export const TEXT_BITMAP_PAD = 0.19;

/** Largest ladder size rendered into a cached bitmap (step 11 = 148 px). */
export const TEXT_CACHE_MAX_STEP = 11;

/** Stroke `lineWidth` for a font size in px: 0.1 × font (≈ font / 20 visible outside the glyph). */
export function textStrokeWidth(fontPx: number): number {
  return Math.max(1, fontPx * 0.1);
}

/** Outline thickness visible on each side of the glyph edge (half the stroke width). */
export function textOutlinePx(fontPx: number): number {
  return textStrokeWidth(fontPx) / 2;
}

// The ladder, grown lazily: LADDER[k] = round(LADDER[k − 1] × 1.25).
const LADDER: number[] = [TEXT_LADDER_BASE_PX];
const LADDER_MAX_STEPS = 64;

function ladder(step: number): number {
  while (LADDER.length <= step) {
    const prev = LADDER[LADDER.length - 1] ?? TEXT_LADDER_BASE_PX;
    LADDER.push(Math.round(prev * TEXT_LADDER_RATIO));
  }
  return LADDER[step] ?? TEXT_LADDER_BASE_PX;
}

/**
 * Ladder index for a font size: the smallest ladder size ≥ `fontPx` (steps below 0 clamp to 0),
 * so a bitmap is only ever drawn downscaled. Upscaling (the old nearest-size pick, up to ×1.125)
 * blurred small names into a grey smear next to agar's crisp white-on-black text.
 */
export function textSizeStep(fontPx: number): number {
  if (!(fontPx > TEXT_LADDER_BASE_PX)) return 0;
  let step = 0;
  while (step < LADDER_MAX_STEPS && ladder(step) < fontPx) step++;
  return step;
}

/**
 * Font size of a ladder step: each step is `round(previous × 1.25)` from 12 px
 * (0 → 12, 1 → 15, 2 → 19, 3 → 24, 4 → 30, 5 → 38 …; architecture §9.4).
 */
export function textSizeForStep(step: number): number {
  if (!(step > 0)) return TEXT_LADDER_BASE_PX;
  return ladder(Math.min(LADDER_MAX_STEPS, Math.round(step)));
}

/** Text fill and outline colours of a theme key. */
export interface TextColors {
  readonly fill: Rgb;
  readonly outline: Rgb;
  /** Stroke width × this (default 1); money labels use a heavier outline to read on any cell. */
  readonly strokeScale?: number;
}

/** Default colours for a key like `classic-light` / `sketch-dark` (unknown keys: classic light). */
export function defaultTextColors(theme: TextTheme): TextColors {
  const [style, mode] = theme.split('-');
  const s: RenderStyle = (RENDER_STYLES as readonly string[]).includes(style ?? '')
    ? (style as RenderStyle)
    : 'classic';
  const c = ARENA_COLORS[s][mode === 'dark' ? 'dark' : 'light'];
  return { fill: c.nameFill, outline: c.nameOutline };
}

function fontFor(px: number, family: string): string {
  return `${TEXT_FONT_WEIGHT} ${px}px ${family}`;
}

/**
 * Draws `text` centred at `(x, y)` in the current transform with the cache's look: outline
 * first, then fill. For text larger than the cache renders.
 */
export function drawTextDirect(
  ctx: Ctx2D,
  text: string,
  x: number,
  y: number,
  fontPx: number,
  colors: TextColors,
  family: string = TEXT_FONT_FAMILY,
): void {
  ctx.font = fontFor(fontPx, family);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 2;
  ctx.lineWidth = textStrokeWidth(fontPx) * (colors.strokeScale ?? 1);
  ctx.strokeStyle = rgbToHex(colors.outline);
  ctx.strokeText(text, x, y);
  ctx.fillStyle = rgbToHex(colors.fill);
  ctx.fillText(text, x, y);
}

export interface TextCacheOptions {
  /** Bitmap factory (default: render/canvas.ts `createCanvas`). */
  readonly createCanvas?: CanvasFactory;
  readonly fontFamily?: string;
  /**
   * Clear the cache when web fonts finish loading, so bitmaps drawn with a fallback font are
   * redrawn in Ubuntu (default true; needs `document.fonts`).
   */
  readonly watchFonts?: boolean;
}

interface Entry extends TextSprite {
  readonly canvas: Canvas2D;
}

export class TextCache {
  readonly maxEntries: number;
  /** Bitmaps rendered since construction (cache misses); for tests and the stats readout. */
  renders = 0;
  private readonly entries = new Map<string, Entry>();
  private readonly colors = new Map<TextTheme, TextColors>();
  private readonly makeCanvas: CanvasFactory;
  private readonly family: string;
  private measureCtx: Ctx2D | null = null;

  constructor(maxEntries = 512, opts: TextCacheOptions = {}) {
    this.maxEntries = Math.max(1, Math.floor(maxEntries));
    this.makeCanvas = opts.createCanvas ?? createCanvas;
    this.family = opts.fontFamily ?? TEXT_FONT_FAMILY;
    if (opts.watchFonts !== false) this.watchFonts();
  }

  get size(): number {
    return this.entries.size;
  }

  /** A cell name bitmap for `text` at ≈ `fontPx`. */
  name(text: string, fontPx: number, theme: TextTheme): TextSprite {
    const step = Math.min(TEXT_CACHE_MAX_STEP, textSizeStep(fontPx));
    return this.get(`n|${theme}|${step}|${text}`, text, step, theme);
  }

  /** A mass label bitmap for `floor(mass)` at ≈ `fontPx`. */
  mass(value: number, fontPx: number, theme: TextTheme): TextSprite {
    const v = Number.isFinite(value) ? Math.floor(value) : 0;
    const step = Math.min(TEXT_CACHE_MAX_STEP, textSizeStep(fontPx));
    return this.get(`m|${theme}|${step}|${v}`, String(v), step, theme);
  }

  /** Drop everything (theme or font change). */
  clear(): void {
    this.entries.clear();
  }

  /** The colours of a theme key (custom ones from `setThemeColors`, else ARENA_COLORS). */
  themeColors(theme: TextTheme): TextColors {
    let c = this.colors.get(theme);
    if (!c) {
      c = defaultTextColors(theme);
      this.colors.set(theme, c);
    }
    return c;
  }

  /** Overrides a theme key's colours (theme.json ink); clears the cache when they change. */
  setThemeColors(theme: TextTheme, fill: Rgb, outline: Rgb, strokeScale = 1): void {
    const prev = this.themeColors(theme);
    if (prev.fill === fill && prev.outline === outline && (prev.strokeScale ?? 1) === strokeScale) {
      return;
    }
    this.colors.set(theme, strokeScale === 1 ? { fill, outline } : { fill, outline, strokeScale });
    this.clear();
  }

  /** True when a bitmap for this name is cached (no LRU touch). */
  hasName(text: string, fontPx: number, theme: TextTheme): boolean {
    const step = Math.min(TEXT_CACHE_MAX_STEP, textSizeStep(fontPx));
    return this.entries.has(`n|${theme}|${step}|${text}`);
  }

  private get(key: string, text: string, step: number, theme: TextTheme): TextSprite {
    const hit = this.entries.get(key);
    if (hit) {
      // LRU: Map keeps insertion order, so re-inserting marks the entry most recently used.
      this.entries.delete(key);
      this.entries.set(key, hit);
      return hit;
    }
    const entry = this.render(text, textSizeForStep(step), this.themeColors(theme));
    this.entries.set(key, entry);
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next();
      if (oldest.done) break;
      this.entries.delete(oldest.value);
    }
    return entry;
  }

  private render(text: string, px: number, colors: TextColors): Entry {
    this.renders++;
    const font = fontFor(px, this.family);
    const stroke = textStrokeWidth(px) * (colors.strokeScale ?? 1);
    const pad = Math.ceil(Math.max(px * TEXT_BITMAP_PAD, stroke / 2 + 1));
    let textW = px * 0.6 * Math.max(1, text.length);
    const m = this.measurer();
    if (m) {
      m.font = font;
      const w = m.measureText(text).width;
      if (Number.isFinite(w) && w > 0) textW = w;
    }
    const width = Math.max(1, Math.ceil(textW + pad * 2));
    const height = Math.max(1, Math.ceil(px * TEXT_BITMAP_HEIGHT));
    const canvas = this.makeCanvas(width, height);
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    const ctx = get2d(canvas);
    if (ctx) {
      ctx.font = font;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'miter';
      ctx.miterLimit = 2;
      ctx.lineWidth = stroke;
      ctx.strokeStyle = rgbToHex(colors.outline);
      ctx.fillStyle = rgbToHex(colors.fill);
      const cx = width / 2;
      const cy = height / 2;
      ctx.strokeText(text, cx, cy);
      ctx.fillText(text, cx, cy);
    }
    return { canvas, width, height, renderedPx: px };
  }

  private measurer(): Ctx2D | null {
    if (this.measureCtx) return this.measureCtx;
    try {
      this.measureCtx = get2d(this.makeCanvas(1, 1));
    } catch {
      this.measureCtx = null;
    }
    return this.measureCtx;
  }

  private watchFonts(): void {
    const fonts = typeof document !== 'undefined' ? (document as Document).fonts : undefined;
    if (!fonts) return;
    try {
      fonts.addEventListener('loadingdone', () => this.clear());
      // Nothing on the page may use Ubuntu yet; ask for it so names switch to it soon.
      void fonts.load(fontFor(24, this.family)).then(
        () => this.clear(),
        () => undefined,
      );
    } catch {
      // Font loading API unavailable: fallback fonts stay.
    }
  }
}
